import { useHasKeyboard, useMediaQuery } from "@/hooks";
import { formatMilesCompact } from "@/lib";
import { TransactionTypes } from "@/stores";
import { useEffect, useRef, useState } from "react";
import { Label } from "../ui/label";
import { Slider } from "../ui/slider";

interface MilesSliderProps {
  totalMiles: number;
  minMiles: number;
  maxMiles: number;
  handleMilesChange: (value: number[]) => void;
  transactionType: TransactionTypes;
  disabled?: boolean;
}

export const MilesSlider = ({
  transactionType,
  totalMiles,
  minMiles,
  maxMiles,
  handleMilesChange,
  disabled = false,
}: MilesSliderProps) => {
  const [inputValue, setInputValue] = useState(totalMiles.toLocaleString());
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSmallScreen = useMediaQuery("(max-width: 640px)");
  const hasKeyboard = useHasKeyboard();
  const [lastTapTime, setLastTapTime] = useState(0);

  const isMobile = isSmallScreen || !hasKeyboard;

  const formatNumberWithCommas = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, "");
    if (!numericValue) return "";
    return parseInt(numericValue, 10).toLocaleString();
  };

  useEffect(() => {
    if (!isFocused) {
      setInputValue(totalMiles.toLocaleString());
    }
  }, [totalMiles, isFocused]);

  const ROUNDING_INCREMENT = 1000;

  return (
    <div className={`${disabled ? "opacity-70" : "opacity-100"}`}>
      <div className="flex items-center justify-between gap-4 mb-2 xl:mb-6">
        <Label className="text-lg font-medium text-gray-900 flex-1">
          Miles For {transactionType === TransactionTypes.BUY && "Purchase"}{" "}
          {transactionType === TransactionTypes.SELL && "Sale"}
        </Label>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            inputMode="numeric"
            pattern="[0-9]*"
            onClick={(e) => {
              if (!hasKeyboard) {
                const now = new Date().getTime();
                const timeSinceLastTap = now - lastTapTime;

                if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
                  e.currentTarget.select();
                }

                setLastTapTime(now);
              }
            }}
            onChange={(e) => {
              setInputValue(formatNumberWithCommas(e.target.value));
            }}
            onFocus={() => {
              setIsFocused(true);
            }}
            onBlur={() => {
              setIsFocused(false);
              const value = inputValue.replace(/[^0-9]/g, "");
              if (value) {
                const numValue = parseInt(value, 10);
                if (!isNaN(numValue)) {
                  const roundedValue =
                    Math.round(numValue / ROUNDING_INCREMENT) *
                    ROUNDING_INCREMENT;
                  const clampedValue = Math.max(
                    minMiles,
                    Math.min(maxMiles, roundedValue),
                  );
                  handleMilesChange([clampedValue]);
                } else {
                  setInputValue(totalMiles.toLocaleString());
                }
              } else {
                setInputValue(totalMiles.toLocaleString());
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
              const allowedKeys = [
                "Backspace",
                "Delete",
                "ArrowLeft",
                "ArrowRight",
                "Tab",
                "Enter",
              ];
              if (!/^\d$/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault();
              }
            }}
            className={`py-2 border ${isFocused ? "border-blue-500 ring-1 ring-blue-200" : "border-gray-300"} rounded-md text-lg font-medium ${disabled ? "opacity-70" : "opacity-100"} text-center min-h-[44px] w-32 transition-all duration-200`}
            disabled={disabled}
            aria-label="Miles amount"
          />
          {isFocused && (
            <div
              className={`absolute ${isMobile ? "-bottom-6 left-0" : "-bottom-6 right-0"} text-xs text-gray-500`}
            >
              {!hasKeyboard
                ? "Tap outside to confirm"
                : "Press Enter to confirm"}
            </div>
          )}
        </div>
      </div>

      <div className="mb-2 xl:mb-4">
        <Slider
          value={[totalMiles]}
          onValueChange={handleMilesChange}
          max={maxMiles}
          min={minMiles}
          step={1000}
          className={`mb-2 xl:mb-4 ${isMobile ? "h-7" : ""}`}
          disabled={disabled}
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>{formatMilesCompact(minMiles)}</span>
          <span>
            {formatMilesCompact((maxMiles - minMiles) / 2 + minMiles)}
          </span>
          <span>{formatMilesCompact(maxMiles)}</span>
        </div>
      </div>
    </div>
  );
};
